/**
 * OTOMATİK EŞİTLEME SAĞLAYICISI — P-a.
 *
 * `CentralProvider`ın İÇİNDE durur (depo ve merkez ikisi de gerekli) ve depo
 * açıldıktan SONRA çizilir (`App.tsx` açılış durumlarını bekliyor). İki tetik:
 *   1. ilk çizim (açılış),
 *   2. `visibilitychange` → `visible` (sekme öne geldi).
 * Periyodik yoklama YOK (K1).
 *
 * "Merkezden eşitle" düğmesi de BURADAN geçer: iki ayrı eşitleme yolu olursa
 * biri kur çekmeyi unutur (eskiden olan buydu) ya da ikisi aynı anda koşar.
 *
 * RAPOR NASIL YENİLENİYOR: eşitleme bitince `central.invalidate()`. Depo ve
 * merkez bağlamı AYNI `revision` sayacını paylaşıyor (`App.tsx`), rapor
 * ekranı depo `revision`ına bağlı — yani rapor yeniden hesaplanır. Bu bir
 * tesadüf değil kilit: `otomatikEsitleme.test.tsx` rapor ekranında düğmeye
 * basmadan sayının değiştiğini ölçüyor.
 *
 * HATA YUTULMAZ: son eşitlemenin zamanı, sebebi ve hataları bağlamda durur,
 * İzleme ekranı gösterir. Merkez yoksa HİÇBİR ŞEY yapılmaz (uçak modu).
 */

import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import type { ReactNode } from "react";
import type { IsoDate } from "@parafon/contracts";
import { useRepositoryContext } from "../repo/RepositoryContext";
import { enEskiIslemGunu, izlenenAracKimlikleri, kurCiftleri } from "../lib/esitlemeHedefi";
import { RAPOR_PARA_BIRIMLERI } from "../lib/raporParaBirimi";
import { useCentral } from "./CentralContext";
import type { CentralContextValue } from "./CentralContext";
import { createEsitleyici } from "./esitleyici";
import type { EsitlemeSebebi } from "./esitleyici";
import type { ChangedFrom, FxSyncResult, SyncReport } from "@parafon/contracts/device-api";
import { mergeChangedFrom } from "@parafon/contracts/local-api";
import { fiyatlarDegisti } from "../lib/seriOnbellegi";
import { M } from "../dil";
import { errorText } from "../lib/rpcError";

export interface EsitlemeKaydi {
  readonly sebep: EsitlemeSebebi;
  /** ISO zaman damgası. */
  readonly baslangic: string;
  readonly sureMs: number;
  /** null → istenecek izlenen araç yoktu. */
  readonly fiyat: SyncReport | null;
  /** null → istenecek kur çifti yoktu. */
  readonly kur: FxSyncResult | null;
  /** Fırlayan çağrılar + raporların kendi hataları. Boşsa sorunsuz. */
  readonly hatalar: readonly string[];
}

export interface EsitlemeDurumu {
  /** Merkez, önbellek ve eşitleme motoru üçü de bağlı mı. */
  readonly kullanilabilir: boolean;
  readonly calisiyor: boolean;
  readonly son: EsitlemeKaydi | null;
  readonly esitle: (sebep: EsitlemeSebebi) => Promise<EsitlemeKaydi | null>;
}

const KULLANILAMAZ: EsitlemeDurumu = {
  kullanilabilir: false,
  calisiyor: false,
  son: null,
  esitle: () => Promise.resolve(null),
};

const EsitlemeBaglami = createContext<EsitlemeDurumu | null>(null);

/** Sağlayıcı yoksa eşitleme YOK — çökmez, düğme kapalı görünür. */
export function useEsitleme(): EsitlemeDurumu {
  return useContext(EsitlemeBaglami) ?? KULLANILAMAZ;
}

/** Yerel takvim günü. `toISOString()` UTC verir; İstanbul'da 00:00–03:00 arası dünü. */
export function bugunYerel(now: Date = new Date()): IsoDate {
  const y = String(now.getFullYear());
  const m = String(now.getMonth() + 1).padStart(2, "0");
  const d = String(now.getDate()).padStart(2, "0");
  return `${y}-${m}-${d}`;
}

function onYilOnce(day: IsoDate): IsoDate {
  return `${String(Number(day.slice(0, 4)) - 10)}${day.slice(4)}`;
}

/**
 * Eşitlemenin grafik önbelleğine etkisi: fiyat ve kur raporlarının
 * "değişimin başı"nın erkeni. İstenecek bir şey yoksa (rapor null) etkisi yok.
 */
export function esitlemeninDegisimi(
  fiyat: SyncReport | null,
  kur: FxSyncResult | null,
  kirildi: boolean,
): ChangedFrom {
  if (kirildi) return "all";
  return mergeChangedFrom(fiyat === null ? null : fiyat.changedFrom, kur === null ? null : kur.changedFrom);
}

function mesaj(err: unknown): string {
  return errorText(err);
}

export function EsitlemeSaglayici({
  children,
  otomatik = true,
}: {
  readonly children: ReactNode;
  /** false → yalnızca düğme. Ekran testleri kendi eşitlemesini yönetir. */
  readonly otomatik?: boolean;
}) {
  const { repo } = useRepositoryContext();
  const central = useCentral();

  const kullanilabilir =
    central.central !== null && central.prices !== null && central.sync !== null;

  // Eşitleyici TEK kez kurulur; çalışma anında en güncel bağlamı okur.
  const baglam = useRef<{ repo: typeof repo; central: CentralContextValue }>({ repo, central });
  baglam.current = { repo, central };

  const [calisiyor, setCalisiyor] = useState(false);
  const [son, setSon] = useState<EsitlemeKaydi | null>(null);
  const takili = useRef(true);
  useEffect(() => {
    takili.current = true;
    return () => {
      takili.current = false;
    };
  }, []);

  const [esitleyici] = useState(() =>
    createEsitleyici<EsitlemeKaydi | null>({
      calistir: async (sebep) => {
        const { repo: r, central: c } = baglam.current;
        if (c.central === null || c.prices === null || c.sync === null) return null;
        if (takili.current) setCalisiyor(true);
        const t0 = Date.now();
        const baslangic = new Date(t0).toISOString();
        const hatalar: string[] = [];
        let fiyat: SyncReport | null = null;
        let kur: FxSyncResult | null = null;
        // Yazım sırasında fırlayan çağrı: neyin yazıldığı bilinmiyor.
        let kirildi = false;
        try {
          const [sayfa, hesaplar] = await Promise.all([
            // Devre dışı hesapların varlıkları da izlenir: devre dışılık yalnızca görünüm (25.10.2026).
            r.transactions.query({ limit: 5000, devreDisiDahil: true }),
            r.accounts.list(true, true),
          ]);
          const rows = sayfa.rows;
          const ids = izlenenAracKimlikleri(rows);
          const to = bugunYerel();

          if (ids.length > 0) {
            try {
              fiyat = await c.sync.syncPrices({
                cache: c.prices,
                central: c.central,
                instrumentIds: ids,
                from: onYilOnce(to),
                to,
                ensureTracked: true,
              });
              hatalar.push(...fiyat.errors);
            } catch (err) {
              hatalar.push(M.kabuk.esitleme.fiyat(mesaj(err)));
              kirildi = true;
            }
          }

          // Aracın para birimi önbellekte (merkezden gelir); fiyat eşitlemesi
          // ONU da yazdığı için kur çiftleri fiyattan SONRA hesaplanır.
          let aracParalari: (string | null)[] = [];
          if (ids.length > 0) {
            try {
              aracParalari = (await c.prices.instrumentStates(ids)).map((e) =>
                e.currency === "?" ? null : e.currency,
              );
            } catch (err) {
              hatalar.push(M.kabuk.esitleme.onbellek(mesaj(err)));
            }
          }
          const ciftler = kurCiftleri({
            transactions: rows,
            accountCurrencies: hesaplar.map((a) => a.currency),
            instrumentCurrencies: aracParalari,
            reportCurrencies: RAPOR_PARA_BIRIMLERI.map((c) => c.value),
          });
          const from = enEskiIslemGunu(rows);
          if (ciftler.length > 0 && from !== null) {
            try {
              kur = await c.sync.syncFx({
                cache: c.prices,
                central: c.central,
                pairs: ciftler,
                from,
                to,
              });
              hatalar.push(...kur.errors);
            } catch (err) {
              hatalar.push(M.kabuk.esitleme.kur(mesaj(err)));
              kirildi = true;
            }
          }
        } catch (err) {
          hatalar.push(M.kabuk.esitleme.kasa(mesaj(err)));
        }

        const kayit: EsitlemeKaydi = {
          sebep,
          baslangic,
          sureMs: Date.now() - t0,
          fiyat,
          kur,
          hatalar,
        };
        if (takili.current) {
          setSon(kayit);
          setCalisiyor(false);
        }
        // GRAFİK ÖNBELLEĞİ: yalnızca gerçekten değişen günden sonrası atılır;
        // aynı fiyatı yeniden yazmak bir şey sildirmez. Fırlayan çağrıda ne
        // yazıldığı bilinmez → hepsi.
        const degisim = esitlemeninDegisimi(fiyat, kur, kirildi);
        fiyatlarDegisti(degisim);
        // Şifreli kaptaki kalıcı kopya: bar/kuru tetikleyiciler zaten siler;
        // künye ve şirket olayı değişimini ancak eşitleme bilir.
        if (degisim !== null) await r.seriOnbellegi?.sil(degisim).catch(() => undefined);
        // Bir şey yazılmamış olsa da yenilenir: "zaten güncel" de bir sonuç ve
        // İzleme ekranındaki "son eşitleme" sütunu değişti. Grafik ise yalnızca
        // önbellekte EKSİK kalan günleri ister.
        baglam.current.central.invalidate();
        return kayit;
      },
    }),
  );

  const esitle = useCallback((sebep: EsitlemeSebebi) => esitleyici.tetikle(sebep), [esitleyici]);

  useEffect(() => {
    if (!otomatik || !kullanilabilir) return undefined;
    void esitle("acilis");
    const doc = globalThis.document;
    if (doc === undefined) return undefined;
    const oneGeldi = (): void => {
      if (doc.visibilityState === "visible") void esitle("one-gelme");
    };
    doc.addEventListener("visibilitychange", oneGeldi);
    return () => doc.removeEventListener("visibilitychange", oneGeldi);
  }, [otomatik, kullanilabilir, esitle]);

  const deger = useMemo<EsitlemeDurumu>(
    () => ({ kullanilabilir, calisiyor, son, esitle }),
    [kullanilabilir, calisiyor, son, esitle],
  );
  return <EsitlemeBaglami.Provider value={deger}>{children}</EsitlemeBaglami.Provider>;
}
